import type { EnumName } from '../../api/types';

/**
 * The five kinds of pilot paperwork, described once.
 *
 * They differ only in which taxonomy they point at and which extra fields they carry, so the
 * list, the form and the expiry traffic light are all shared. Adding a sixth kind means adding
 * an entry here, not another screen.
 */
export type DocumentKind = 'licence' | 'rating' | 'medical' | 'language' | 'certificate';

export type DocumentKindSpec = {
  kind: DocumentKind;
  /** API collection, relative to `/api/v1/`. */
  path: 'licences' | 'ratings' | 'medicals' | 'language-proficiencies' | 'certificates';
  /** Taxonomy endpoint backing the type picker, when there is one. */
  taxonomyPath?:
    | 'taxonomy/licence-types'
    | 'taxonomy/rating-types'
    | 'taxonomy/certificate-types'
    | 'taxonomy/medical-classes';
  /** Field on the record holding the taxonomy foreign key. */
  typeField?: 'licence_type' | 'rating_type' | 'certificate_type' | 'medical_class';
  titleKey: string;
  addKey: string;
  emptyKey: string;
  /** Reference-number-style field, labelled per kind. */
  referenceField?: {
    name: 'licence_number' | 'certificate_number' | 'reference';
    labelKey: string;
  };
  /** Who issued it, labelled per kind. */
  issuerField?: {
    name: 'issuing_authority' | 'examiner_name' | 'assessment_body' | 'issuing_body';
    labelKey: string;
  };
  /** The date the document was earned, which drives the derived expiry. */
  issuedField?: {
    name: 'issued_on' | 'examination_date' | 'assessed_on' | 'completed_on';
    labelKey: string;
  };
  /** Enum used for a kind-specific dropdown, currently only the discipline filter. */
  filterEnum?: EnumName;
};

export const DOCUMENT_KINDS: Record<DocumentKind, DocumentKindSpec> = {
  licence: {
    kind: 'licence',
    path: 'licences',
    taxonomyPath: 'taxonomy/licence-types',
    typeField: 'licence_type',
    titleKey: 'staff.licences',
    addKey: 'staff.addLicence',
    emptyKey: 'staff.noLicences',
    referenceField: { name: 'licence_number', labelKey: 'staff.licenceNumber' },
    issuerField: { name: 'issuing_authority', labelKey: 'staff.issuingAuthority' },
    issuedField: { name: 'issued_on', labelKey: 'common.validFrom' },
    filterEnum: 'discipline',
  },
  rating: {
    kind: 'rating',
    path: 'ratings',
    taxonomyPath: 'taxonomy/rating-types',
    typeField: 'rating_type',
    titleKey: 'staff.ratings',
    addKey: 'staff.addRating',
    emptyKey: 'staff.noRatings',
    issuedField: { name: 'issued_on', labelKey: 'common.validFrom' },
    filterEnum: 'rating_kind',
  },
  medical: {
    kind: 'medical',
    path: 'medicals',
    taxonomyPath: 'taxonomy/medical-classes',
    typeField: 'medical_class',
    titleKey: 'staff.medicals',
    addKey: 'staff.addMedical',
    emptyKey: 'staff.noMedical',
    referenceField: { name: 'certificate_number', labelKey: 'staff.licenceNumber' },
    issuerField: { name: 'examiner_name', labelKey: 'staff.issuingAuthority' },
    issuedField: { name: 'examination_date', labelKey: 'staff.examinationDate' },
  },
  language: {
    kind: 'language',
    path: 'language-proficiencies',
    titleKey: 'staff.language',
    addKey: 'staff.addLanguage',
    emptyKey: 'staff.noLanguage',
    issuerField: { name: 'assessment_body', labelKey: 'staff.issuingAuthority' },
    issuedField: { name: 'assessed_on', labelKey: 'staff.examinationDate' },
  },
  certificate: {
    kind: 'certificate',
    path: 'certificates',
    taxonomyPath: 'taxonomy/certificate-types',
    typeField: 'certificate_type',
    titleKey: 'staff.certificates',
    addKey: 'staff.addCertificate',
    emptyKey: 'staff.noCertificates',
    referenceField: { name: 'reference', labelKey: 'settings.code' },
    issuerField: { name: 'issuing_body', labelKey: 'staff.issuingAuthority' },
    issuedField: { name: 'completed_on', labelKey: 'staff.completedOn' },
  },
};

export const DOCUMENT_ORDER: DocumentKind[] = [
  'licence',
  'rating',
  'medical',
  'language',
  'certificate',
];
